import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as XLSXStyle from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import CompanyLocationSelector from '../Components/CompanyLocationSelector';
import Sidebar from '../Components/Sidebar';
import trident from '../assets/odin-trident.png';
import { getUserRoles, logout } from '../auth/cookieUtils';
import api, {
  downloadContent,
  fetchBlobUrl,
  openContent,
  uploadBoilerDocument,
  uploadReport,
} from '../api/boilerMatrixApi';
import BoilerMatrixManager from './BoilerMatrixManager';
import { buildBoilerMatrixExportRows } from '../utils/exportRows';
import './BoilerMatricesPage.css';
import './OdinWorkspaceTheme.css';

const REPORT_TYPES = [
  ['PRIMARY', 'Primary inspection report'],
  ['BOILER_INSPECTOR', 'Boiler inspector report'],
  ['NDE_CONTRACTOR', 'NDE / contractor report'],
  ['OTHER', 'Other inspection report'],
];

const EVIDENCE_TYPES = ['RT', 'UT', 'PT', 'MT', 'VT', 'MLO', 'VISUAL', 'OTHER'];
const INSPECTION_INTERVAL_OPTIONS = Array.from({ length: 10 }, (_, index) => index + 1);
const MAX_INSPECTION_INTERVAL_YEARS = 100;

const intervalSelection = (value) => {
  const years = Number(value);
  if (!Number.isInteger(years) || years < 1) return { choice: '', customYears: '' };
  return years <= 10
    ? { choice: String(years), customYears: '' }
    : { choice: 'CUSTOM', customYears: String(years) };
};

const resolvedIntervalYears = (choice, customYears) => {
  const years = Number(choice === 'CUSTOM' ? customYears : choice);
  return Number.isInteger(years) && years >= 1 && years <= MAX_INSPECTION_INTERVAL_YEARS
    ? years
    : null;
};

function InspectionIntervalFields({
  choice,
  customYears,
  onChoiceChange,
  onCustomYearsChange,
  disabled = false,
  allowUnset = false,
  help,
}) {
  return (
    <div className="inspection-interval-fields">
      <label>
        Inspection interval
        <select disabled={disabled} value={choice} onChange={(event) => onChoiceChange(event.target.value)}>
          <option value="">{allowUnset ? 'Not set' : 'Select interval'}</option>
          {INSPECTION_INTERVAL_OPTIONS.map((years) => (
            <option value={years} key={years}>{years} {years === 1 ? 'year' : 'years'}</option>
          ))}
          <option value="CUSTOM">Custom</option>
        </select>
      </label>
      {choice === 'CUSTOM' && (
        <label className="custom-inspection-interval">
          Custom interval
          <span>
            <input
              type="number"
              inputMode="numeric"
              min="1"
              max={MAX_INSPECTION_INTERVAL_YEARS}
              step="1"
              disabled={disabled}
              value={customYears}
              onChange={(event) => onCustomYearsChange(event.target.value)}
              aria-label="Custom inspection interval in years"
            />
            <strong>years</strong>
          </span>
        </label>
      )}
      {help && <small className="inspection-interval-help">{help}</small>}
    </div>
  );
}

const BOILER_TYPES = [
  ['Recovery Boiler', 'Recovery Boiler'],
  ['Power Boiler', 'Power Boiler'],
  ['Package Boiler', 'Package Boiler'],
  ['OTHER', 'Other'],
];

const DOCUMENT_CATEGORIES = [
  ['designDocuments', 'Design Documents'],
  ['designPhoto', 'Design Photo'],
  ['tminDocuments', 'T-Min Calculations'],
  ['ventOverflowDocuments', 'Vent / Overflow Documents'],
  ['inspectionProtocolDocuments', 'Inspection Protocols'],
  ['additionalPhotos', 'Additional Photos'],
];

const getErrorMessage = (error) =>
  error?.response?.data?.message || error?.response?.data?.detail || error?.message || 'Something went wrong.';

const formatDate = (value) => {
  if (!value) return 'Not recorded';
  const dateOnly = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
};

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const rangeLabel = (ranges = []) => ranges
  .map((range) => range.startPage === range.endPage
    ? `p. ${range.startPage}`
    : `pp. ${range.startPage}–${range.endPage}`)
  .join(', ');

const createExportWorksheet = (rows) => {
  const exportRows = rows.length > 0 ? rows : [{ Message: 'No records available' }];
  const worksheet = XLSXStyle.utils.json_to_sheet(exportRows);
  const headers = Object.keys(exportRows[0]);
  const range = XLSXStyle.utils.decode_range(worksheet['!ref']);

  worksheet['!autofilter'] = {
    ref: XLSXStyle.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: range.e.r, c: range.e.c } }),
  };
  worksheet['!cols'] = headers.map((header) => {
    const longestValue = Math.max(
      header.length,
      ...exportRows.map((row) => String(row[header] ?? '').length)
    );
    return { wch: Math.min(Math.max(longestValue + 2, 12), 48) };
  });

  for (let column = 0; column <= range.e.c; column += 1) {
    const headerCell = worksheet[XLSXStyle.utils.encode_cell({ r: 0, c: column })];
    if (headerCell) {
      headerCell.s = {
        fill: { fgColor: { rgb: '1F6F67' } },
        font: { color: { rgb: 'FFFFFF' }, bold: true },
        alignment: { vertical: 'center', wrapText: true },
        border: { bottom: { style: 'thin', color: { rgb: '173C4D' } } },
      };
    }
  }

  for (let row = 1; row <= range.e.r; row += 1) {
    for (let column = 0; column <= range.e.c; column += 1) {
      const cell = worksheet[XLSXStyle.utils.encode_cell({ r: row, c: column })];
      if (cell) {
        cell.s = {
          fill: { fgColor: { rgb: row % 2 === 0 ? 'EEF5F7' : 'FFFFFF' } },
          alignment: { vertical: 'top', wrapText: true },
          border: { bottom: { style: 'hair', color: { rgb: 'CAD7DE' } } },
        };
      }
    }
  }

  return worksheet;
};

const exportBoilerMatrixToExcel = ({ boiler, matrix }) => {
  const matrixRows = buildBoilerMatrixExportRows(matrix);

  const workbook = XLSXStyle.utils.book_new();
  XLSXStyle.utils.book_append_sheet(workbook, createExportWorksheet(matrixRows), 'Matrix Lines');

  const excelBuffer = XLSXStyle.write(workbook, { bookType: 'xlsx', type: 'array' });
  const safeName = (boiler.name || 'boiler').trim().replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '') || 'boiler';
  saveAs(
    new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    `${safeName}_matrix.xlsx`
  );
};

function Modal({
  title,
  onClose,
  wide = false,
  className = '',
  eyebrow,
  description,
  children,
}) {
  return (
    <div className="boiler-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className={`boiler-modal ${wide ? 'boiler-modal-wide' : ''} ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="boiler-modal-header">
          <div>
            {eyebrow && <span className="boiler-modal-eyebrow">{eyebrow}</span>}
            <h2>{title}</h2>
            {description && <p>{description}</p>}
          </div>
          <button type="button" className="boiler-icon-button" onClick={onClose} aria-label="Close">×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function AddBoilerModal({ locationId, onClose, onCreated }) {
  const roles = getUserRoles();
  const accessLabel = roles.includes('SUPER_ADMIN') ? 'SUPER ADMIN' : 'ADMIN';
  const [form, setForm] = useState({
    name: '',
    assetNumber: '',
    boilerType: 'Recovery Boiler',
    otherBoilerType: '',
    description: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    const selectedType = form.boilerType === 'OTHER'
      ? form.otherBoilerType.trim()
      : form.boilerType;

    if (!selectedType) {
      setError('Enter the boiler type when Other is selected.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const created = await api.createBoiler(locationId, {
        name: form.name.trim(),
        assetNumber: form.assetNumber.trim() || null,
        boilerType: selectedType,
        description: form.description.trim() || null,
      });
      onCreated(created);
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Add a boiler"
      eyebrow={accessLabel}
      description="Create the equipment record now; reports and equipment files can be added immediately."
      className="add-boiler-modal"
      onClose={onClose}
    >
      <form className="boiler-form add-boiler-form" onSubmit={submit}>
        {error && <div className="boiler-error full-row">{error}</div>}

        <label>
          Boiler name
          <input
            required
            autoFocus
            value={form.name}
            placeholder="Example: #5 Recovery Boiler"
            onChange={(event) => updateForm('name', event.target.value)}
          />
        </label>

        <label>
          Asset ID
          <input
            value={form.assetNumber}
            placeholder="Example: RB-05"
            onChange={(event) => updateForm('assetNumber', event.target.value)}
          />
        </label>

        <label className={form.boilerType === 'OTHER' ? '' : 'boiler-type-field'}>
          Boiler type
          <select
            required
            value={form.boilerType}
            onChange={(event) => updateForm('boilerType', event.target.value)}
          >
            {BOILER_TYPES.map(([value, label]) => (
              <option value={value} key={value}>{label}</option>
            ))}
          </select>
        </label>

        {form.boilerType === 'OTHER' && (
          <label>
            Other boiler type
            <input
              required
              value={form.otherBoilerType}
              placeholder="Enter the boiler type"
              onChange={(event) => updateForm('otherBoilerType', event.target.value)}
            />
          </label>
        )}

        <label className="full-row">
          Description
          <textarea
            rows="5"
            value={form.description}
            placeholder="Optional equipment or service notes"
            onChange={(event) => updateForm('description', event.target.value)}
          />
        </label>

        <div className="add-boiler-location-note full-row">
          <span aria-hidden="true">i</span>
          <p>
            The new record will be added to the currently selected location
            {locationId ? ` (location ID ${locationId})` : ''}, and its standard boiler matrix will be created automatically.
          </p>
        </div>

        <div className="boiler-modal-actions full-row">
          <button type="button" className="boiler-button secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="boiler-button add-boiler-submit" disabled={saving}>
            {saving ? 'Adding…' : 'Add boiler record'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function EvidenceMapper({ boiler, reports, matrix, initialReportId, initialLineIds, editing, replacingEvidence, onClose, onSaved }) {
  const suggestedReplacementReport = replacingEvidence
    ? reports.find((report) => report.currentReport && Number(report.id) !== Number(replacingEvidence.reportId))
      || reports.find((report) => Number(report.id) !== Number(replacingEvidence.reportId))
    : null;
  const initialMatrixLine = matrix.find((line) => (initialLineIds || [])
    .some((lineId) => Number(lineId) === Number(line.id)));
  const suggestedLineMethod = initialMatrixLine?.methods
    ?.map((method) => String(method).trim().toUpperCase().replaceAll(' ', '_'))
    .find((method) => EVIDENCE_TYPES.includes(method));
  const initialInterval = intervalSelection(initialMatrixLine?.inspectionIntervalYears);
  const [reportId, setReportId] = useState(editing?.reportId || initialReportId || suggestedReplacementReport?.id || reports[0]?.id || '');
  const [title, setTitle] = useState(editing?.title || replacingEvidence?.title || 'Inspection report evidence');
  const [evidenceType, setEvidenceType] = useState(editing?.evidenceType || replacingEvidence?.evidenceType || suggestedLineMethod || 'VT');
  const [intervalChoice, setIntervalChoice] = useState(initialInterval.choice);
  const [customIntervalYears, setCustomIntervalYears] = useState(initialInterval.customYears);
  const [ranges, setRanges] = useState(editing?.ranges?.length ? editing.ranges : [{ startPage: 1, endPage: 1 }]);
  const [selectedLineIds, setSelectedLineIds] = useState(() => new Set(editing?.matrixLineIds || initialLineIds || []));
  const [lineSearch, setLineSearch] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewError, setPreviewError] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(ranges[0]?.startPage || 1);
  const selectedReport = reports.find((report) => Number(report.id) === Number(reportId));

  useEffect(() => {
    let active = true;
    let objectUrl = '';
    if (!reportId) return undefined;
    setPreviewError('');
    setPreviewUrl('');
    fetchBlobUrl(`/api/boilers/inspection-reports/${reportId}/content`, { download: false })
      .then((url) => {
        objectUrl = url;
        if (active) setPreviewUrl(url);
      })
      .catch((requestError) => active && setPreviewError(getErrorMessage(requestError)));
    return () => {
      active = false;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [reportId]);

  const visibleLines = useMemo(() => {
    const query = lineSearch.trim().toLowerCase();
    if (!query) return matrix;
    return matrix.filter((line) => [line.groupName, line.subgroup, line.item, line.inspectionDescription]
      .filter(Boolean).join(' ').toLowerCase().includes(query));
  }, [lineSearch, matrix]);

  const updateRange = (index, key, value) => {
    const next = ranges.map((range, rangeIndex) => rangeIndex === index
      ? { ...range, [key]: Math.max(1, Number(value) || 1) }
      : range);
    setRanges(next);
    setCurrentPage(next[index].startPage);
  };

  const toggleLine = (lineId) => {
    setSelectedLineIds((current) => {
      const next = new Set(current);
      if (next.has(lineId)) next.delete(lineId); else next.add(lineId);
      return next;
    });
  };

  const save = async () => {
    if (!reportId) return setError('Select an inspection report.');
    if (!selectedReport) return setError('The selected inspection report is no longer available.');
    if (!title.trim()) return setError('Enter an evidence title.');
    if (!editing && selectedLineIds.size === 0) return setError('Select at least one matrix line.');
    const inspectionIntervalYears = resolvedIntervalYears(intervalChoice, customIntervalYears);
    if (!editing && inspectionIntervalYears === null) {
      return setError('Select an inspection interval between 1 and 100 years.');
    }
    if (!Number.isInteger(Number(selectedReport.pageCount)) || Number(selectedReport.pageCount) < 1) {
      return setError('The selected report does not have a valid PDF page count.');
    }
    if (ranges.some((range) => range.startPage > range.endPage || range.endPage > Number(selectedReport.pageCount))) {
      return setError(`Page ranges must stay within pages 1 through ${selectedReport.pageCount}.`);
    }
    try {
      setSaving(true);
      setError('');
      if (editing) {
        await api.updateEvidence(editing.id, { title: title.trim(), evidenceType, ranges });
      } else {
        await api.createEvidence(boiler.id, {
          reportId: Number(reportId), title: title.trim(), evidenceType, ranges,
          matrixLineIds: Array.from(selectedLineIds),
          replaceEvidenceIds: replacingEvidence ? [replacingEvidence.id] : [],
          inspectionIntervalYears,
        });
      }
      await onSaved();
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={editing ? 'Edit Evidence Page Ranges' : replacingEvidence ? 'Replace Linked Evidence' : 'Create Linked Evidence'}
      eyebrow="REPORT EVIDENCE"
      description="Select readable report pages, confirm the inspection method, and link the evidence to the appropriate matrix lines."
      className="evidence-mapper-modal"
      onClose={onClose}
      wide
    >
      <div className="evidence-mapper">
        <section className="evidence-preview-panel">
          <div className="evidence-preview-toolbar">
            <label>
              Source inspection report
              <select disabled={Boolean(editing)} value={reportId} onChange={(e) => setReportId(e.target.value)}>
                <option value="">Select a report</option>
                {reports.map((report) => (
                  <option key={report.id} value={report.id}>{report.title} · {formatDate(report.inspectionDate)}</option>
                ))}
              </select>
            </label>
            {selectedReport && <span>{selectedReport.pageCount} pages · {String(selectedReport.reportType || 'OTHER').replaceAll('_', ' ')}</span>}
          </div>
          <div className="evidence-page-controls">
            <button type="button" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>Previous</button>
            <label>Page <input type="number" min="1" max={selectedReport?.pageCount || 1} value={currentPage} onChange={(e) => setCurrentPage(Math.max(1, Number(e.target.value) || 1))} /></label>
            <button type="button" onClick={() => setCurrentPage((p) => Math.min(selectedReport?.pageCount || 1, p + 1))}>Next</button>
          </div>
          <div className="evidence-pdf-frame">
            {previewUrl ? (
              <iframe key={`${previewUrl}-${currentPage}`} title="Inspection report page preview" src={`${previewUrl}#page=${currentPage}&zoom=page-width`} />
            ) : previewError ? <div className="boiler-error">{previewError}</div> : <div className="boiler-loading">Loading full-size PDF preview…</div>}
          </div>
        </section>

        <aside className="evidence-map-controls">
          {error && <div className="boiler-error">{error}</div>}
          {replacingEvidence && (
            <p className="evidence-replacement-note">
              Save the new evidence first; ODIN will then unlink “{replacingEvidence.title}” from the selected matrix line. The old evidence and report stay in history and any links to other matrix lines are unchanged.
            </p>
          )}
          <label>Evidence title<input value={title} onChange={(e) => setTitle(e.target.value)} /></label>
          <label>Inspection method
            <select value={evidenceType} onChange={(e) => setEvidenceType(e.target.value)}>
              {EVIDENCE_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
            </select>
            {suggestedLineMethod && !editing && !replacingEvidence && (
              <small className="evidence-method-hint">
                Preselected from “{initialMatrixLine.item}”. You can change it before saving.
              </small>
            )}
          </label>
          {!editing && (
            <InspectionIntervalFields
              choice={intervalChoice}
              customYears={customIntervalYears}
              onChoiceChange={(value) => {
                setIntervalChoice(value);
                if (value !== 'CUSTOM') setCustomIntervalYears('');
              }}
              onCustomYearsChange={setCustomIntervalYears}
              disabled={saving}
              help="ODIN adds this interval to the newest linked report date for every selected matrix line."
            />
          )}
          <div className="evidence-range-heading">
            <strong>PDF page ranges</strong>
            <button type="button" onClick={() => setRanges([...ranges, { startPage: currentPage, endPage: currentPage }])}>+ Add range</button>
          </div>
          {ranges.map((range, index) => (
            <div className="evidence-range-row" key={`${index}-${range.startPage}-${range.endPage}`}>
              <input aria-label="Start page" type="number" min="1" max={selectedReport?.pageCount} value={range.startPage} onChange={(e) => updateRange(index, 'startPage', e.target.value)} />
              <span>through</span>
              <input aria-label="End page" type="number" min="1" max={selectedReport?.pageCount} value={range.endPage} onChange={(e) => updateRange(index, 'endPage', e.target.value)} />
              <button type="button" disabled={ranges.length === 1} onClick={() => setRanges(ranges.filter((_, i) => i !== index))}>×</button>
            </div>
          ))}

          {!editing && !replacingEvidence && (
            <div className="evidence-line-picker">
              <div className="evidence-range-heading"><strong>Linked matrix lines</strong><span>{selectedLineIds.size} selected</span></div>
              <input placeholder="Search matrix lines…" value={lineSearch} onChange={(e) => setLineSearch(e.target.value)} />
              <div className="evidence-line-list">
                {visibleLines.map((line) => (
                  <label key={line.id}>
                    <input type="checkbox" checked={selectedLineIds.has(line.id)} onChange={() => toggleLine(line.id)} />
                    <span><strong>{line.item}</strong><small>{line.groupName}{line.subgroup ? ` · ${line.subgroup}` : ''}</small></span>
                  </label>
                ))}
              </div>
            </div>
          )}
          {editing && <p className="boiler-help">The source report stays fixed so this evidence keeps a reliable audit trail. Use “Edit a copy” from a shared item to make a line-specific revision.</p>}
          <div className="boiler-modal-actions sticky">
            <button type="button" className="boiler-button secondary" onClick={onClose}>Cancel</button>
            <button type="button" className="boiler-button" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save evidence'}</button>
          </div>
        </aside>
      </div>
    </Modal>
  );
}

function EvidenceManager({ line, evidenceLibrary, isAdmin, onClose, onRefresh, onCreate, onEdit, onReplace }) {
  const [reuseId, setReuseId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notApplicable, setNotApplicable] = useState(Boolean(line.notApplicable));
  const [notes, setNotes] = useState(line.notes || '');
  const initialInterval = intervalSelection(line.inspectionIntervalYears);
  const [intervalChoice, setIntervalChoice] = useState(initialInterval.choice);
  const [customIntervalYears, setCustomIntervalYears] = useState(initialInterval.customYears);
  const linkedIds = new Set(line.evidence.map((item) => item.id));
  const available = evidenceLibrary.filter((item) => item.active && !linkedIds.has(item.id));

  const act = async (operation) => {
    try {
      setBusy(true);
      setError('');
      await operation();
      await onRefresh();
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setBusy(false);
    }
  };

  const saveLineSettings = () => {
    const inspectionIntervalYears = intervalChoice
      ? resolvedIntervalYears(intervalChoice, customIntervalYears)
      : null;
    if (intervalChoice && inspectionIntervalYears === null) {
      setError('Enter a custom inspection interval between 1 and 100 years.');
      return;
    }
    act(() => api.updateMatrixLine(line.id, {
      notApplicable,
      notes: notes.trim() || null,
      inspectionIntervalYears,
    }));
  };

  const editCopy = async (item) => {
    try {
      setBusy(true);
      setError('');
      const copy = await api.cloneEvidence(item.id, line.id);
      await api.unlinkEvidence(line.id, item.id);
      await onRefresh();
      onEdit(copy);
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="evidence-drawer-backdrop" role="presentation" onMouseDown={onClose}>
      <aside className="evidence-drawer" onMouseDown={(event) => event.stopPropagation()}>
        <div className="boiler-modal-header">
          <div><small>{line.groupName}</small><h2>Matrix Line Details</h2><p>{line.item}</p></div>
          <button type="button" className="boiler-icon-button" onClick={onClose}>×</button>
        </div>
        {error && <div className="boiler-error">{error}</div>}
        <div className="evidence-drawer-section">
          <h3>Linked evidence</h3>
          {line.evidence.length === 0 && <p className="boiler-empty-inline">No evidence is linked to this matrix line.</p>}
          {line.evidence.map((item) => (
            <article className="linked-evidence-card" key={item.id}>
              <div><strong>{item.title}</strong><span>{item.evidenceType} · {item.reportTitle}</span><small>{formatDate(item.reportInspectionDate)} · {rangeLabel(item.ranges)} · revision {item.revision}</small></div>
              <div className="linked-evidence-actions">
                <button type="button" onClick={() => openContent(`/api/boilers/evidence/${item.id}/content`)}>Preview section</button>
                <button type="button" onClick={() => downloadContent(`/api/boilers/evidence/${item.id}/content`, `${item.title}.pdf`)}>Download</button>
                {isAdmin && <button type="button" className="replace" onClick={() => onReplace(item)}>Replace</button>}
                {isAdmin && <button type="button" onClick={() => onEdit(item)}>Edit</button>}
                {isAdmin && item.matrixLineIds.length > 1 && (
                  <button type="button" disabled={busy} onClick={() => editCopy(item)}>Edit a copy</button>
                )}
                {isAdmin && (
                  <button type="button" className="danger" disabled={busy} onClick={() => {
                    if (window.confirm('Unlink this evidence from this matrix line? The evidence remains available everywhere else.')) {
                      act(() => api.unlinkEvidence(line.id, item.id));
                    }
                  }}>Unlink</button>
                )}
              </div>
            </article>
          ))}
        </div>
        {isAdmin && (
          <>
            <div className="evidence-drawer-section">
              <button type="button" className="boiler-button full" onClick={onCreate}>Create evidence from PDF pages</button>
            </div>
            <div className="evidence-drawer-section matrix-line-settings">
              <div className="matrix-line-settings-heading">
                <h3>Line status and notes</h3>
                <span className={`matrix-line-status ${notApplicable ? 'na' : 'applicable'}`}>
                  {notApplicable ? 'N/A' : 'Applicable'}
                </span>
              </div>
              <label className="matrix-line-na-toggle">
                <input
                  type="checkbox"
                  checked={notApplicable}
                  disabled={busy}
                  onChange={(event) => setNotApplicable(event.target.checked)}
                />
                <span>
                  <strong>Mark this matrix line N/A</strong>
                  <small>This keeps existing evidence and history but identifies the inspection line as not applicable.</small>
                </span>
              </label>
              <InspectionIntervalFields
                choice={intervalChoice}
                customYears={customIntervalYears}
                onChoiceChange={(value) => {
                  setIntervalChoice(value);
                  if (value !== 'CUSTOM') setCustomIntervalYears('');
                }}
                onCustomYearsChange={setCustomIntervalYears}
                disabled={busy}
                allowUnset
                help={line.lastInspectionDate
                  ? `Last inspection ${formatDate(line.lastInspectionDate)} · next inspection ${line.nextInspectionDue ? formatDate(line.nextInspectionDue) : 'will be calculated after saving'}`
                  : 'The next inspection will be calculated when dated evidence is linked.'}
              />
              <label className="matrix-line-notes-field">
                Notes
                <textarea
                  value={notes}
                  maxLength="10000"
                  disabled={busy}
                  placeholder="Add context, justification, inspection guidance, or follow-up notes for this line…"
                  onChange={(event) => setNotes(event.target.value)}
                />
                <small>{notes.length.toLocaleString()} / 10,000 characters</small>
              </label>
              <button
                type="button"
                className="boiler-button full"
                disabled={busy}
                onClick={saveLineSettings}
              >
                {busy ? 'Saving…' : 'Save line settings'}
              </button>
              {line.settingsUpdatedAt && (
                <small className="matrix-line-settings-audit">
                  Last updated {formatDateTime(line.settingsUpdatedAt)}
                  {line.settingsUpdatedBy ? ` by ${line.settingsUpdatedBy}` : ''}
                </small>
              )}
            </div>
            <div className="evidence-drawer-section">
              <h3>Reuse evidence from another line</h3>
              <p className="boiler-help">Link a saved section without creating another copy of the same report pages.</p>
              <select value={reuseId} onChange={(e) => setReuseId(e.target.value)}>
                <option value="">Select saved evidence</option>
                {available.map((item) => <option key={item.id} value={item.id}>{item.title} · {item.reportTitle} · {rangeLabel(item.ranges)}</option>)}
              </select>
              <button type="button" className="boiler-button full" disabled={!reuseId || busy} onClick={() => act(() => api.linkEvidence(Number(reuseId), [line.id]))}>Link selected evidence</button>
            </div>
          </>
        )}
        {!isAdmin && (
          <div className="evidence-drawer-section matrix-line-settings">
            <div className="matrix-line-settings-heading">
              <h3>Line status and notes</h3>
              <span className={`matrix-line-status ${line.notApplicable ? 'na' : 'applicable'}`}>
                {line.notApplicable ? 'N/A' : 'Applicable'}
              </span>
            </div>
            {line.notes
              ? <p className="matrix-line-saved-note">{line.notes}</p>
              : <p className="boiler-empty-inline">No notes have been added to this matrix line.</p>}
            <p className="matrix-line-schedule-summary">
              <strong>Inspection interval:</strong>{' '}
              {line.inspectionIntervalYears
                ? `${line.inspectionIntervalYears} ${line.inspectionIntervalYears === 1 ? 'year' : 'years'}`
                : 'Not set'}
              <br />
              <strong>Next inspection:</strong>{' '}
              {line.notApplicable
                ? 'N/A'
                : line.nextInspectionDue
                  ? formatDate(line.nextInspectionDue)
                  : 'Not scheduled'}
            </p>
            {line.settingsUpdatedAt && (
              <small className="matrix-line-settings-audit">
                Last updated {formatDateTime(line.settingsUpdatedAt)}
                {line.settingsUpdatedBy ? ` by ${line.settingsUpdatedBy}` : ''}
              </small>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}

function ReportsTab({ boiler, reports, isAdmin, onRefresh, onMap }) {
  const [showUpload, setShowUpload] = useState(false);
  const [form, setForm] = useState({ title: '', reportType: 'PRIMARY', inspectionDate: '', currentReport: true, file: null });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      setError('');
      await uploadReport(boiler.id, form);
      setForm({ title: '', reportType: 'PRIMARY', inspectionDate: '', currentReport: true, file: null });
      setShowUpload(false);
      await onRefresh();
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section>
      <div className="boiler-section-header">
        <div><h2>Inspection Reports</h2><p>Every report remains in the boiler’s history and can be used as an evidence source.</p></div>
        {isAdmin && <button type="button" className="boiler-button" onClick={() => setShowUpload(!showUpload)}>Upload inspection report</button>}
      </div>
      {showUpload && (
        <form className="report-upload-form" onSubmit={submit}>
          {error && <div className="boiler-error full-row">{error}</div>}
          <label>Report title<input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></label>
          <label>Report type<select value={form.reportType} onChange={(e) => setForm({ ...form, reportType: e.target.value })}>{REPORT_TYPES.map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label>
          <label>Inspection date<input required type="date" value={form.inspectionDate} onChange={(e) => setForm({ ...form, inspectionDate: e.target.value })} /></label>
          <label>PDF file<input required type="file" accept="application/pdf,.pdf" onChange={(e) => setForm({ ...form, file: e.target.files[0] })} /></label>
          <label className="boiler-checkbox"><input type="checkbox" checked={form.currentReport} onChange={(e) => setForm({ ...form, currentReport: e.target.checked })} /> Mark as current report</label>
          <button className="boiler-button" disabled={saving}>{saving ? 'Uploading…' : 'Upload report'}</button>
        </form>
      )}
      <div className="report-list">
        {reports.length === 0 && <div className="boiler-empty">No inspection reports have been uploaded for this boiler.</div>}
        {reports.map((report) => (
          <article className="report-row" key={report.id}>
            <div><h3>{report.title}</h3><p>{String(report.reportType || 'OTHER').replaceAll('_', ' ')} · Inspected {formatDate(report.inspectionDate)}</p><small>Uploaded by {report.uploadedBy || 'Unknown'} · {report.pageCount ?? 0} pages</small></div>
            <span className={`report-status ${report.currentReport ? 'current' : ''}`}>{report.currentReport ? 'Current' : 'Historical'}</span>
            <div className="report-actions">
              <button type="button" onClick={() => openContent(`/api/boilers/inspection-reports/${report.id}/content`)}>Preview</button>
              <button type="button" onClick={() => downloadContent(`/api/boilers/inspection-reports/${report.id}/content`, report.fileName)}>Download</button>
              {isAdmin && <button type="button" className="primary" onClick={() => onMap(report.id)}>Map evidence</button>}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function DocumentsTab({ boiler, documents, isAdmin, onRefresh }) {
  const [category, setCategory] = useState('designDocuments');
  const [displayName, setDisplayName] = useState('');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      setError('');
      await uploadBoilerDocument(boiler.id, category, displayName, file);
      setDisplayName('');
      setFile(null);
      event.target.reset();
      await onRefresh();
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section>
      <div className="boiler-section-header"><div><h2>Boiler Documents</h2><p>The same supporting document types available for other ODIN equipment.</p></div></div>
      {isAdmin && (
        <form className="document-upload-form" onSubmit={submit}>
          {error && <div className="boiler-error full-row">{error}</div>}
          <label>Document type<select value={category} onChange={(e) => setCategory(e.target.value)}>{DOCUMENT_CATEGORIES.map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label>
          <label>Display name<input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Optional" /></label>
          <label>File<input required type="file" onChange={(e) => setFile(e.target.files[0])} /></label>
          <button className="boiler-button" disabled={saving || !file}>{saving ? 'Uploading…' : 'Upload document'}</button>
        </form>
      )}
      <div className="document-grid">
        {DOCUMENT_CATEGORIES.map(([value, label]) => {
          const files = documents.filter((document) => document.category === value);
          return (
            <article className="document-card" key={value}>
              <h3>{label}</h3>
              {files.length === 0 && <p className="boiler-empty-inline">No files uploaded.</p>}
              {files.map((document) => (
                <div className="document-row" key={document.id}>
                  <div><strong>{document.displayName || document.fileName}</strong><small>{formatDate(document.uploadedAt)}</small></div>
                  <div>
                    <button type="button" onClick={() => openContent(`/api/boilers/documents/${document.id}/content`)}>Preview</button>
                    <button type="button" onClick={() => downloadContent(`/api/boilers/documents/${document.id}/content`, document.fileName)}>Download</button>
                    {isAdmin && <button type="button" className="danger" onClick={async () => {
                      if (!window.confirm('Delete this boiler document?')) return;
                      try {
                        setError('');
                        await api.deleteDocument(document.id);
                        await onRefresh();
                      } catch (requestError) {
                        setError(getErrorMessage(requestError));
                      }
                    }}>Delete</button>}
                  </div>
                </div>
              ))}
            </article>
          );
        })}
      </div>
    </section>
  );
}

function MatrixTab({ boiler, matrix, evidence, isAdmin, onManage, onManageMatrix }) {
  const [search, setSearch] = useState('');
  const [group, setGroup] = useState('ALL');
  const [method, setMethod] = useState('ALL');
  const [evidenceStatus, setEvidenceStatus] = useState('ALL');
  const groups = useMemo(() => Array.from(new Set(matrix.map((line) => line.groupName))), [matrix]);
  const methods = useMemo(() => Array.from(new Set(
    matrix.flatMap((line) => line.methods || [])
  )).filter(Boolean).sort(), [matrix]);
  const filtered = matrix.filter((line) => {
    const matchesGroup = group === 'ALL' || line.groupName === group;
    const matchesMethod = method === 'ALL' || (line.methods || []).includes(method);
    const hasEvidence = line.evidence.length > 0;
    const matchesEvidence = evidenceStatus === 'ALL'
      || (evidenceStatus === 'LINKED' && hasEvidence)
      || (evidenceStatus === 'UNLINKED' && !hasEvidence);
    const text = [line.item, line.groupName, line.subgroup, line.inspectionDescription, line.frequency, line.notes].filter(Boolean).join(' ').toLowerCase();
    return matchesGroup && matchesMethod && matchesEvidence && text.includes(search.trim().toLowerCase());
  });

  const handleExport = () => {
    try {
      exportBoilerMatrixToExcel({ boiler, matrix });
    } catch (exportError) {
      console.error('Boiler matrix export failed:', exportError);
      window.alert('The boiler matrix could not be exported. Please try again.');
    }
  };

  return (
    <section>
      <div className={`matrix-toolbar ${isAdmin ? 'with-manager' : ''}`}>
        <input aria-label="Search matrix" placeholder="Search matrix…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select aria-label="Inspection area" value={group} onChange={(e) => setGroup(e.target.value)}><option value="ALL">All inspection areas</option>{groups.map((name) => <option value={name} key={name}>{name}</option>)}</select>
        <select aria-label="Inspection method" value={method} onChange={(e) => setMethod(e.target.value)}>
          <option value="ALL">All inspection methods</option>
          {methods.map((name) => <option value={name} key={name}>{name.replaceAll('_', ' ')}</option>)}
        </select>
        <select aria-label="Evidence status" value={evidenceStatus} onChange={(e) => setEvidenceStatus(e.target.value)}>
          <option value="ALL">All evidence</option>
          <option value="LINKED">Linked only</option>
          <option value="UNLINKED">Unlinked only</option>
        </select>
        <button
          type="button"
          className="boiler-button matrix-export-button"
          disabled={matrix.length === 0}
          onClick={handleExport}
        >
          Export matrix
        </button>
        {isAdmin && <button type="button" className="boiler-button matrix-manager-open" onClick={onManageMatrix}>Manage matrix</button>}
      </div>
      <div className="matrix-table-wrap">
        <table className="boiler-matrix-table">
          <thead><tr><th>Inspection area / line</th><th>Methods</th><th>Inspection description</th><th>Frequency</th><th>Last inspection</th><th>Next inspection</th><th>Report evidence</th></tr></thead>
          <tbody>
            {filtered.map((line, index) => {
              const showGroup = index === 0 || filtered[index - 1].groupName !== line.groupName || filtered[index - 1].subgroup !== line.subgroup;
              return (
                <React.Fragment key={line.id}>
                  {showGroup && <tr className="matrix-group-row"><td colSpan="7"><strong>{line.groupName}</strong>{line.subgroup && <span>{line.subgroup}</span>}</td></tr>}
                  <tr className={line.notApplicable ? 'matrix-line-na' : ''}>
                    <td className="matrix-item">
                      <div className="matrix-item-heading">
                        <span>{line.item}</span>
                        {line.notApplicable && <span className="matrix-line-status na">N/A</span>}
                        {line.notes && <span className="matrix-note-indicator" title={line.notes}>Note</span>}
                      </div>
                    </td>
                    <td><div className="method-tags">{line.methods.map((method) => <span key={method}>{method}</span>)}</div></td>
                    <td>{line.inspectionDescription || '—'}</td>
                    <td>{line.frequency || '—'}</td>
                    <td className={line.notApplicable ? 'matrix-na-date' : line.lastInspectionDate ? '' : 'not-recorded'}>
                      {line.notApplicable ? 'N/A' : formatDate(line.lastInspectionDate)}
                    </td>
                    <td className={line.notApplicable ? 'matrix-na-date' : line.nextInspectionDue ? '' : 'not-recorded'}>
                      {line.notApplicable
                        ? 'N/A'
                        : line.nextInspectionDue
                          ? formatDate(line.nextInspectionDue)
                          : line.inspectionIntervalYears
                            ? 'Awaiting inspection date'
                            : 'Interval not set'}
                    </td>
                    <td>
                      <div className="matrix-evidence-cell">
                        {line.evidence.slice(0, 2).map((item) => <button type="button" className="evidence-chip" key={item.id} title={`${item.reportTitle} · ${rangeLabel(item.ranges)}`} onClick={() => openContent(`/api/boilers/evidence/${item.id}/content`)}>{item.evidenceType} · {rangeLabel(item.ranges)}</button>)}
                        {line.evidence.length > 2 && <span>+{line.evidence.length - 2} more</span>}
                        <button type="button" className="manage-evidence-button" onClick={() => onManage(line)}>{isAdmin ? 'Manage' : 'View'}</button>
                      </div>
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {filtered.length === 0 && <div className="boiler-empty">No matrix lines match the selected filters.</div>}
      <span className="sr-only">{evidence.length} evidence records available</span>
    </section>
  );
}

export default function BoilerMatricesPage() {
  const roles = getUserRoles();
  const isAdmin = roles.includes('ADMIN') || roles.includes('SUPER_ADMIN');
  const [selectedLocationId, setSelectedLocationId] = useState(() => {
    const storedLocationId = Number(sessionStorage.getItem('selectedLocationId'));
    return Number.isInteger(storedLocationId) && storedLocationId > 0 ? storedLocationId : null;
  });
  const [boilers, setBoilers] = useState([]);
  const [activeBoiler, setActiveBoiler] = useState(null);
  const [matrix, setMatrix] = useState([]);
  const [reports, setReports] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [tab, setTab] = useState('matrix');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAddBoiler, setShowAddBoiler] = useState(false);
  const [showMatrixManager, setShowMatrixManager] = useState(false);
  const [managedLine, setManagedLine] = useState(null);
  const [mapper, setMapper] = useState(null);

  const loadBoilers = useCallback(async (locationId) => {
    if (!locationId) return setBoilers([]);
    try {
      setLoading(true);
      setError('');
      const data = await api.listBoilers(locationId);
      setBoilers(Array.isArray(data) ? data : []);
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setActiveBoiler(null);
    setManagedLine(null);
    setShowMatrixManager(false);
    setMapper(null);
    loadBoilers(selectedLocationId);
  }, [selectedLocationId, loadBoilers]);

  const loadBoilerData = useCallback(async (boiler) => {
    if (!boiler) return;
    try {
      setLoading(true);
      setError('');
      const [matrixData, reportData, evidenceData, documentData] = await Promise.all([
        api.getMatrix(boiler.id), api.listReports(boiler.id), api.listEvidence(boiler.id), api.listDocuments(boiler.id),
      ]);
      const nextMatrix = Array.isArray(matrixData) ? matrixData : [];
      setMatrix(nextMatrix);
      setReports(Array.isArray(reportData) ? reportData : []);
      setEvidence(Array.isArray(evidenceData) ? evidenceData : []);
      setDocuments(Array.isArray(documentData) ? documentData : []);
      if (managedLine) setManagedLine(nextMatrix.find((line) => line.id === managedLine.id) || null);
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, [managedLine]);

  const openBoiler = async (boiler) => {
    setActiveBoiler(boiler);
    setShowMatrixManager(false);
    setTab('matrix');
    await loadBoilerData(boiler);
  };

  const refresh = async () => loadBoilerData(activeBoiler);
  const refreshMatrix = async () => {
    if (activeBoiler) {
      const data = await api.getMatrix(activeBoiler.id);
      setMatrix(Array.isArray(data) ? data : []);
    }
  };
  const refreshReports = async () => {
    const data = await api.listReports(activeBoiler.id);
    setReports(Array.isArray(data) ? data : []);
    await loadBoilers(selectedLocationId);
  };
  const refreshDocuments = async () => {
    const data = await api.listDocuments(activeBoiler.id);
    setDocuments(Array.isArray(data) ? data : []);
  };

  return (
    <div className="dashboard-page odin-workspace-page boiler-page">
      <div className="dashboard-topbar">
        <div className="topbar-left"><img src={trident} alt="ODIN Logo" className="navbar-logo" /><CompanyLocationSelector onLocationChange={setSelectedLocationId} /></div>
        <div className="topbar-center"><span className="page-title">Boiler Matrices</span></div>
        <div className="topbar-right"><button className="logout-button" onClick={logout}>Logout</button></div>
      </div>
      <div className="dashboard-main">
        <Sidebar />
        <main className="dashboard-content boiler-content">
          {error && <div className="boiler-error page-error">{error}</div>}
          {!selectedLocationId ? (
            <div className="boiler-empty">Select a company and location to view its boilers.</div>
          ) : !activeBoiler ? (
            <section className="boiler-selector" aria-labelledby="boiler-selector-title">
              <header className="boiler-selector-header">
                <div>
                  <span className="boiler-selector-eyebrow">Boiler Matrix</span>
                  <h1 id="boiler-selector-title">Select a Boiler</h1>
                  <p>Choose a boiler to open its inspection matrix, reports, linked evidence, and documents.</p>
                </div>
                {isAdmin && (
                  <button
                    type="button"
                    className="boiler-button boiler-add-button"
                    onClick={() => setShowAddBoiler(true)}
                  >
                    <span aria-hidden="true">+</span> Add Boiler
                  </button>
                )}
              </header>

              {loading ? <div className="boiler-loading">Loading boilers…</div> : (
                <div className="boiler-card-grid">
                  {boilers.map((boiler) => (
                    <button
                      type="button"
                      className="boiler-card"
                      key={boiler.id}
                      onClick={() => openBoiler(boiler)}
                      aria-label={`Open ${boiler.name} boiler matrix`}
                    >
                      <div className="boiler-card-heading">
                        <span className="boiler-card-mark" aria-hidden="true">
                          {(boiler.name || 'B').charAt(0).toUpperCase()}
                        </span>
                        <span className="boiler-card-type">{boiler.boilerType || 'Boiler'}</span>
                      </div>

                      <div className="boiler-card-copy">
                        <h2>{boiler.name}</h2>
                        <p className="boiler-card-asset">{boiler.assetNumber || 'No asset number'}</p>
                        {boiler.description && <p className="boiler-card-description">{boiler.description}</p>}
                      </div>

                      <dl>
                        <div>
                          <dt>Last inspection</dt>
                          <dd>{formatDate(boiler.lastInspectionDate)}</dd>
                        </div>
                        <div>
                          <dt>Inspection reports</dt>
                          <dd>{boiler.reportCount ?? 0}</dd>
                        </div>
                      </dl>

                      <span className="boiler-open-label">
                        <span>Open boiler matrix</span>
                        <span aria-hidden="true">→</span>
                      </span>
                    </button>
                  ))}
                  {boilers.length === 0 && (
                    <div className="boiler-empty boiler-selector-empty">
                      <strong>No boilers have been added for this location.</strong>
                      {isAdmin && <span> Use Add Boiler to create the first boiler matrix.</span>}
                    </div>
                  )}
                </div>
              )}
            </section>
          ) : (
            <>
              <div className="boiler-detail-header">
                <button type="button" className="boiler-back-button" onClick={() => setActiveBoiler(null)}>← All boilers</button>
                <div><span>{activeBoiler.boilerType || 'Boiler'}{activeBoiler.assetNumber ? ` · ${activeBoiler.assetNumber}` : ''}</span><h1>{activeBoiler.name}</h1></div>
              </div>
              <nav className="boiler-tabs" aria-label="Boiler sections">
                <button type="button" className={tab === 'matrix' ? 'active' : ''} onClick={() => setTab('matrix')}>Inspection Matrix</button>
                <button type="button" className={tab === 'reports' ? 'active' : ''} onClick={() => setTab('reports')}>Inspection Reports</button>
                <button type="button" className={tab === 'documents' ? 'active' : ''} onClick={() => setTab('documents')}>Boiler Documents</button>
              </nav>
              {loading ? <div className="boiler-loading">Loading boiler data…</div> : (
                <>
                  {tab === 'matrix' && <MatrixTab boiler={activeBoiler} matrix={matrix} evidence={evidence} isAdmin={isAdmin} onManage={setManagedLine} onManageMatrix={() => setShowMatrixManager(true)} />}
                  {tab === 'reports' && <ReportsTab boiler={activeBoiler} reports={reports} isAdmin={isAdmin} onRefresh={refreshReports} onMap={(reportId) => setMapper({ reportId, lineIds: [] })} />}
                  {tab === 'documents' && <DocumentsTab boiler={activeBoiler} documents={documents} isAdmin={isAdmin} onRefresh={refreshDocuments} />}
                </>
              )}
            </>
          )}
        </main>
      </div>

      {showAddBoiler && <AddBoilerModal locationId={selectedLocationId} onClose={() => setShowAddBoiler(false)} onCreated={(created) => { setShowAddBoiler(false); setBoilers((current) => [...current, created].sort((a, b) => a.name.localeCompare(b.name))); openBoiler(created); }} />}
      {showMatrixManager && activeBoiler && <BoilerMatrixManager boiler={activeBoiler} boilers={boilers} onClose={() => setShowMatrixManager(false)} onChanged={refreshMatrix} />}
      {managedLine && <EvidenceManager line={managedLine} evidenceLibrary={evidence} isAdmin={isAdmin} onClose={() => setManagedLine(null)} onRefresh={refresh} onCreate={() => setMapper({ lineIds: [managedLine.id] })} onEdit={(item) => setMapper({ editing: item })} onReplace={(item) => setMapper({ lineIds: [managedLine.id], replacingEvidence: item })} />}
      {mapper && <EvidenceMapper boiler={activeBoiler} reports={reports} matrix={matrix} initialReportId={mapper.reportId} initialLineIds={mapper.lineIds} editing={mapper.editing} replacingEvidence={mapper.replacingEvidence} onClose={() => setMapper(null)} onSaved={async () => { setMapper(null); await refresh(); }} />}
    </div>
  );
}
